import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle, XCircle, Trash2, Eye, ArrowLeft, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  none: 'bg-gray-100 text-gray-600 border-gray-200',
};

const STATUS_COLORS = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
  deleted: 'bg-gray-100 text-gray-500',
};

export default function ModerationDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, sRes] = await Promise.all([
        fetch(`${API}/api/moderation/queue?status=${filter}&limit=50`, { credentials: 'include' }),
        fetch(`${API}/api/moderation/stats`, { credentials: 'include' }),
      ]);
      if (qRes.ok) { const d = await qRes.json(); setItems(d.items || []); }
      if (sRes.ok) { const d = await sRes.json(); setStats(d); }
    } catch { /* silent */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleAction = async (contentId, action) => {
    try {
      const res = await fetch(`${API}/api/moderation/review/${contentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        toast({ title: `Content ${action}d` });
        fetchQueue();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Action failed', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    }
  };

  if (!user) return <div className="flex items-center justify-center min-h-screen text-gray-500">Please log in</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-gray-400 hover:text-gray-600"><ArrowLeft className="w-5 h-5" /></Link>
            <Shield className="w-6 h-6 text-[#FF6B35]" />
            <h1 className="text-xl font-bold text-gray-900">Content Moderation</h1>
          </div>
          <button onClick={fetchQueue} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="mod-refresh">
            <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="mod-stats">
            {[
              { label: 'Pending', value: stats.pending, color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: 'Approved', value: stats.approved, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Rejected', value: stats.rejected, color: 'text-red-600', bg: 'bg-red-50' },
              { label: 'Total', value: stats.total, color: 'text-gray-700', bg: 'bg-gray-50' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4 border border-gray-100`}>
                <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2" data-testid="mod-filters">
          {['pending', 'approved', 'rejected', 'deleted', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${filter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              data-testid={`mod-filter-${f}`}>
              {f}
            </button>
          ))}
        </div>

        {/* Queue */}
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-xl p-5 animate-pulse"><div className="h-4 bg-gray-100 rounded w-1/3 mb-3" /><div className="h-3 bg-gray-100 rounded w-2/3" /></div>
          ))}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16" data-testid="mod-empty">
            <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No {filter === 'all' ? '' : filter} items</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="mod-queue">
            {items.map(item => (
              <div key={item.content_id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`mod-item-${item.content_id}`}>
                <div className="p-4 sm:p-5">
                  {/* Top row */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.none}`}>
                        {item.severity?.toUpperCase()}
                      </span>
                      <span className="text-[10px] font-medium text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">
                        {item.content_type}
                      </span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[item.status] || ''}`}>
                        {item.status}
                      </span>
                    </div>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                      {item.flagged_at ? new Date(item.flagged_at).toLocaleDateString() : ''}
                    </span>
                  </div>

                  {/* Content */}
                  <p className="text-sm text-gray-800 bg-gray-50 rounded-lg p-3 mb-3 leading-relaxed">{item.text}</p>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                    <span>By: <strong className="text-gray-600">{item.user_name}</strong></span>
                    {item.reason && <span>Reason: <strong className="text-gray-600">{item.reason}</strong></span>}
                    {item.categories?.length > 0 && (
                      <span>Categories: {item.categories.map(c => (
                        <span key={c} className="ml-1 bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{c}</span>
                      ))}</span>
                    )}
                  </div>

                  {/* Actions */}
                  {item.status === 'pending' && (
                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <button onClick={() => handleAction(item.content_id, 'approve')}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition-colors"
                        data-testid={`mod-approve-${item.content_id}`}>
                        <CheckCircle className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button onClick={() => handleAction(item.content_id, 'reject')}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-xs font-semibold transition-colors"
                        data-testid={`mod-reject-${item.content_id}`}>
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                      <button onClick={() => handleAction(item.content_id, 'delete')}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs font-semibold transition-colors"
                        data-testid={`mod-delete-${item.content_id}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  )}
                  {item.status !== 'pending' && item.reviewed_by && (
                    <p className="text-[10px] text-gray-400 pt-2 border-t border-gray-50">
                      Reviewed by {item.reviewed_by} on {item.reviewed_at ? new Date(item.reviewed_at).toLocaleDateString() : 'N/A'}
                      {item.review_note ? ` — "${item.review_note}"` : ''}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
